import { useCallback, useEffect, useMemo, useState } from "react";
import { getStoredUserLocation } from "../lib/authStorage";

const STORAGE_KEY = "dealswapify_marketplace_filters";
const EVENT_NAME = "dealswapify:filters-changed";

export const DEFAULT_MARKETPLACE_FILTERS = {
  language: "English",
  country: "",
  city: "",
  nearMe: false,
};

const readFilters = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    return {
      ...DEFAULT_MARKETPLACE_FILTERS,
      ...(stored && typeof stored === "object" ? stored : {}),
    };
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return { ...DEFAULT_MARKETPLACE_FILTERS };
  }
};

const persistFilters = (filters) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  window.dispatchEvent(
    new CustomEvent(EVENT_NAME, { detail: filters })
  );
};


export const readSavedUserLocation = () => {
  const { country, city } = getStoredUserLocation();
  return { country, city };
};

export const itemMatchesMarketplaceFilters = (item, filters) => {
  if (!item) return false;

  const itemCountry = String(
    item.locCountry || item.country || ""
  ).trim().toLowerCase();
  const itemCity = String(
    item.locCity || item.city || ""
  ).trim().toLowerCase();

  const selectedCountry = String(filters?.country || "")
    .trim()
    .toLowerCase();
  const selectedCity = String(filters?.city || "")
    .trim()
    .toLowerCase();

  if (selectedCountry && itemCountry !== selectedCountry) {
    return false;
  }

  if (selectedCity && itemCity !== selectedCity) {
    return false;
  }

  return true;
};

const useMarketplaceFilters = () => {
  const [filters, setFilters] = useState(readFilters);

  useEffect(() => {
    const sync = (event) => {
      if (event?.detail) {
        setFilters({
          ...DEFAULT_MARKETPLACE_FILTERS,
          ...event.detail,
        });
        return;
      }

      setFilters(readFilters());
    };

    const syncStorage = (event) => {
      if (!event.key || event.key === STORAGE_KEY) {
        setFilters(readFilters());
      }
    };

    window.addEventListener(EVENT_NAME, sync);
    window.addEventListener("storage", syncStorage);

    return () => {
      window.removeEventListener(EVENT_NAME, sync);
      window.removeEventListener("storage", syncStorage);
    };
  }, []);

  const updateFilters = useCallback((updates) => {
    setFilters((current) => {
      const next = {
        ...current,
        ...(typeof updates === "function" ? updates(current) : updates),
      };

      persistFilters(next);
      return next;
    });
  }, []);

  const resetFilters = useCallback(() => {
    const next = { ...DEFAULT_MARKETPLACE_FILTERS };
    persistFilters(next);
    setFilters(next);
  }, []);

  const hasLocationFilter = useMemo(
    () => Boolean(filters.country || filters.city),
    [filters.country, filters.city]
  );

  return {
    filters,
    updateFilters,
    resetFilters,
    hasLocationFilter,
  };
};

export default useMarketplaceFilters;
